import { EProblemReportStatus, EProductTourTargets, IProblemReportOptions } from '@constants';
import { IBasicUser, IReport } from '@sdk/contracts';
import { dateProvider } from '@utils';
import { Drawer, Empty, Table } from 'antd';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ProblemReportTitle } from '../ProblemReportTitle';
import { StatusCell } from '../StatusCell';

import { FullPageLoader } from '@/components/Loaders';
import { ManualQuestionModal } from '@/components/ManualQuestionModal';
import { useIsMobile } from '@/hooks';
import { useProblemReportsDetail } from '@/store';

export const ProblemReportsDrawer = () => {
  const { t } = useTranslation(['DefaultTranslations', 'ProblemReportsTranslations']);
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const problemReportId = searchParams.get('problemReportId');
  const { isLoading, problemReport } = useProblemReportsDetail(problemReportId);
  const navigateTo = useNavigate();
  const { pathname } = useLocation();

  const closeDrawer = () => navigateTo(pathname);

  const DRAWER_WIDTH = isMobile ? '100%' : '75%';

  if (!problemReportId) return null;

  const getResourceTypeLabel = (reports?: IReport[]): string => {
    const activityType = reports?.[0]?.activity?.type;
    if (activityType) {
      return String(t(`ProblemReportsTranslations:${activityType}`)).toLowerCase();
    }
    return String(t('ProblemReportsTranslations:QUESTION')).toLowerCase();
  };

  const drawerTitle = isLoading
    ? String(t('ProblemReportsTranslations:LOADING_TITLE'))
    : t('ProblemReportsTranslations:DRAWER_TITLE', {
        resourceType: getResourceTypeLabel(problemReport?.reports)
      });

  return (
    <Drawer open title={drawerTitle} width={DRAWER_WIDTH} onClose={closeDrawer}>
      {isLoading ? (
        <FullPageLoader />
      ) : problemReport ? (
        <div>
          <ProblemReportTitle
            resource={problemReport.reports[0].activity || problemReport.reports[0].question}
            course={problemReport.course}
            section={problemReport.section}
          />
          <Table
            rowKey={'_id'}
            tableLayout="fixed"
            pagination={false}
            loading={isLoading}
            columns={columns(t, problemReportId, problemReport.resourceTitle)}
            locale={{
              emptyText: <Empty description={t('ProblemReportsTranslations:NO_PROBLEM_REPORTS')} />
            }}
            dataSource={problemReport.reports}
          />
        </div>
      ) : (
        <span>{t('ProblemReportsTranslations:PROBLEM_REPORT_NOT_FOUND')}</span>
      )}
      <ManualQuestionModal />
    </Drawer>
  );
};

const columns = (
  t: TFunction<['DefaultTranslations' | 'ProblemReportsTranslations']>,
  problemReportId: string,
  resourceTitle: string
) => {
  return [
    {
      title: t('ProblemReportsTranslations:TITLE'),
      dataIndex: 'title',
      render: (title: IProblemReportOptions) => <span>{t(title)}</span>
    },
    {
      title: t('ProblemReportsTranslations:DESCRIPTION'),
      dataIndex: 'description',
      render: (description?: string) => <span>{description}</span>
    },
    {
      title: t('ProblemReportsTranslations:REPORTED_BY'),
      dataIndex: 'reportedBy',
      width: 150,
      render: (reportedBy: IBasicUser) => <span>{`${reportedBy.name} ${reportedBy.lastName}`}</span>
    },
    {
      title: t('ProblemReportsTranslations:STATUS'),
      dataIndex: 'status',
      width: 150,
      render: (status: EProblemReportStatus, row: IReport, index: number) => (
        <StatusCell
          status={status}
          problemReportId={problemReportId}
          reportId={row._id}
          id={index === 0 ? EProductTourTargets.PROBLEM_REPORT_STATUS_CELL : undefined}
          userName={`${row.reportedBy.name} ${row.reportedBy.lastName}`}
          activityName={resourceTitle}
        />
      )
    },
    {
      title: t('ProblemReportsTranslations:CREATION_DATE'),
      dataIndex: 'createdAt',
      width: 150,
      render: (createdAt: Date) => dateProvider.format(createdAt, 'dd/MM/yyyy')
    }
  ];
};
